import { faCheck } from '@fortawesome/pro-solid-svg-icons'

import { Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { renderHeadline, resolveNextStepsCopy } from '../lib/nextStepsCopy'
import { NextStepsActions } from './NextStepsActions'

interface NextStepsCardProps {
  /** The raw `tax_treatment` answer; unknown because intake answers are untyped. */
  taxTreatment: unknown
  attentionCount: number
  /** Scheduler link for the consult CTA; null withholds the CTA entirely. */
  consultUrl: string | null
  /** Qualification treatment, for the CTA event; decided by the report. */
  verdict: string
  /** Whether this practice qualifies for a consult; decided by the API at intake submit. */
  isPql: boolean
}

/**
 * Bottom-of-report CTA card — bridges the report's findings to what Heard
 * actually does about them. Two paths: a free consult to see if Heard's a
 * fit, or go straight to plan options if you're ready now. Copy varies by tax
 * entity; the destinations below never do.
 */
export const NextStepsCard: React.FC<NextStepsCardProps> = ({
  taxTreatment,
  attentionCount,
  consultUrl,
  verdict,
  isPql,
}) => {
  const copy = resolveNextStepsCopy(taxTreatment)
  return (
    // Announced because the copy swaps from the default to the entity variant once the
    // intake answers resolve — a reader who has already heard the generic version would
    // otherwise get no signal it changed.
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-moss-0 to-green-0 p-5"
    >
      <img
        src="https://heard-images.s3.amazonaws.com/assets/book-magic.svg"
        alt=""
        /* Decorative, so it's the first thing to give up when vertical space
           is short — this card gets pinned, and every pixel it spends is a
           pixel of the viewport it might not have. Sized by viewport height:
           its own 195x180 when there's room, 140 when there's less, gone
           below that. */
        className="hidden w-full shrink-0 self-center [@media(min-height:760px)]:block [@media(min-height:760px)]:max-w-[140px] [@media(min-height:900px)]:max-w-[195px]"
      />
      <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
        {copy.eyebrow}
      </Text>
      <Text variant={TypographyStyles.DISPLAY_XS} color={TextColors.HEADER}>
        {renderHeadline(copy.headline, attentionCount)}
      </Text>
      <div className="flex flex-col gap-2">
        {copy.benefits.map((line) => (
          <div key={line} className="flex items-start gap-2">
            <Icon
              icon={faCheck}
              size="2xs"
              color="text-green-100"
              className="mt-0.5"
            />
            <Text variant={TypographyStyles.BODY_SM}>{line}</Text>
          </div>
        ))}
      </div>
      {/* `mt-2` on top of the container's `gap-4` for 24px of separation
            from the benefits, so the actions read as a block of their own. */}
      <NextStepsActions
        taxTreatment={taxTreatment}
        consultUrl={consultUrl}
        verdict={verdict}
        isPql={isPql}
        className="mt-2"
      />
    </div>
  )
}
